/**
 * License management page for e-learning orders.
 * Allows viewing seats, sending invitations, and managing participants.
 */

import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { CoreLink } from '@uncinc/react-drupal-core';
import {
  Button,
  FormTextField,
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';

import InformationOverlay from '../../../../../components/InformationOverlay';
import InvitationEditForm from '../InvitationEditForm';
import StatusIndicator from '../StatusIndicator';
import LicenseExpiryBanner from './LicenseExpiryBanner';
import StatusInfoContent from './StatusInfoContent';
import useOrderInvitations from '../hooks/useOrderInvitations';
import { ROUTE_ACCOUNT_ORDERS } from '../../../../../config/routes';

import type { ElearningInvitation } from '../../../../../types/orders';

import './style.scss';

interface OrderLicensesDetailProps {
  orderId: string;
}

interface InviteFormValues {
  email: string;
}

const OrderLicensesDetail: React.FC<OrderLicensesDetailProps> = ({ orderId }) => {
  const history = useHistory();
  const { t } = useTranslation();

  const {
    order,
    invitations,
    invitationsData,
    seatStats,
    isLoading,
    error,
    isSubmitting,
    invitationError,
    clearInvitationError,
    createInvite,
    updateInvite,
    deleteInvite,
  } = useOrderInvitations(orderId);

  /**
   * Maps API error codes to translation keys.
   * Falls back to generic error message for unknown codes.
   */
  const getErrorMessageKey = useCallback((errorCode: string | null): string => {
    const errorKeyMap: Record<string, string> = {
      VALIDATION_FAILED: 'mijn_vnl.orders.errors.validation_failed',
      INVALID_EMAIL: 'mijn_vnl.orders.errors.invalid_email',
      EMAIL_CONTAINS_PLUS: 'mijn_vnl.orders.errors.email_contains_plus',
      NO_SEATS_AVAILABLE: 'mijn_vnl.orders.errors.no_seats_available',
      EMAIL_ALREADY_INVITED: 'mijn_vnl.orders.errors.email_already_invited',
      USER_ALREADY_ENROLLED: 'mijn_vnl.orders.errors.user_already_enrolled',
      MOODLE_ENROLLMENT_FAILED: 'mijn_vnl.orders.errors.moodle_enrollment_failed',
      INVALID_STATUS: 'mijn_vnl.orders.errors.invalid_status',
      MOODLE_UPDATE_FAILED: 'mijn_vnl.orders.errors.moodle_update_failed',
    };

    return errorKeyMap[errorCode || ''] || 'mijn_vnl.orders.errors.generic';
  }, []);

  // Memoized email validation schema
  const validationSchema = useMemo(
    () => Yup.object().shape({
      email: Yup.string()
        .email(t('mijn_vnl.orders.email_invalid'))
        .required(t('mijn_vnl.orders.email_required')),
    }),
    [t],
  );

  const handleBackClick = useCallback(() => {
    history.push(ROUTE_ACCOUNT_ORDERS);
  }, [history]);

  const handleInviteSubmit = useCallback(
    async (
      values: InviteFormValues,
      { resetForm }: { resetForm: () => void },
    ) => {
      const success = await createInvite(values.email);
      if (success) {
        resetForm();
      }
    },
    [createInvite],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="OrderLicensesDetail OrderLicensesDetail--loading">
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  // Error state
  if (error || !order) {
    return (
      <div className="OrderLicensesDetail OrderLicensesDetail--error">
        <p>{t('mijn_vnl.orders.fetch_error')}</p>
        <Button onClick={handleBackClick}>
          {t('mijn_vnl.orders.back_to_list')}
        </Button>
      </div>
    );
  }

  return (
    <div className="OrderLicensesDetail">
      {/* Back navigation */}
      <div className="OrderLicensesDetail__back">
        <CoreLink href={ROUTE_ACCOUNT_ORDERS} className="OrderLicensesDetail__back-link">
          <i className="icon icon-chevron-right OrderLicensesDetail__back-icon" />
          {t('mijn_vnl.orders.back_to_list_short')}
        </CoreLink>
      </div>

      {/* Header with product info */}
      <div className="OrderLicensesDetail__header">
        <div className="OrderLicensesDetail__header-content">
          <h1 className="OrderLicensesDetail__title">{order.product_title}</h1>

          {/* Seat statistics */}
          <div className="OrderLicensesDetail__meta">
            <span className="OrderLicensesDetail__meta-seats">
              {t('mijn_vnl.orders.seats_purchased_count', {
                count: seatStats.purchased,
              })}
            </span>
            <span className="OrderLicensesDetail__meta-separator" />
            <span className="OrderLicensesDetail__meta-invited">
              {seatStats.used === 0
                ? t('mijn_vnl.orders.participants_invited_none')
                : t('mijn_vnl.orders.participants_invited', {
                  invited: seatStats.used,
                  total: seatStats.purchased,
                })}
            </span>
          </div>
        </div>

        {/* Product image */}
        {order.product_image_url && (
          <div className="OrderLicensesDetail__header-image">
            <img src={order.product_image_url} alt={order.product_title || ''} />
          </div>
        )}
      </div>

      {/* Email invitation form - only visible when seats are available */}
      {seatStats.canInvite ? (
        <div className="OrderLicensesDetail__invite-form">
          <FormikWrapper
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={handleInviteSubmit}
          >
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label>{t('mijn_vnl.orders.invite_email_placeholder')}</label>
            <div className="OrderLicensesDetail__invite-form-row">
              <div className="OrderLicensesDetail__invite-form-field">
                <FormTextField
                  fieldName="email"
                  type="email"
                  disabled={isSubmitting}
                />
              </div>
              <Button
                type="submit"
                className="Button--primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('general.loading')
                  : t('mijn_vnl.orders.send_invitation')}
              </Button>
            </div>
          </FormikWrapper>
        </div>
      ) : (
        <div className="OrderLicensesDetail__no-seats-message">
          <p>{t('mijn_vnl.orders.no_seats_available')}</p>
        </div>
      )}

      {/* Invitation error message */}
      {invitationError.errorCode && (
        <div className="OrderLicensesDetail__invitation-error" role="alert">
          <p className="OrderLicensesDetail__invitation-error-message">
            {t(getErrorMessageKey(invitationError.errorCode))}
          </p>
        </div>
      )}

      {/* Invitations section */}
      <div className="OrderLicensesDetail__invitations">
        <h2 className="OrderLicensesDetail__invitations-title">
          {t('mijn_vnl.orders.who_has_access')}
        </h2>

        {/* License expiry banner (conditional) */}
        <LicenseExpiryBanner
          expiryTimestamp={invitationsData?.license_expiry_date}
          t={t}
        />

        {invitations.length === 0 ? (
          <div className="OrderLicensesDetail__invitations-empty">
            <p>{t('mijn_vnl.orders.no_invitations_yet')}</p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="OrderLicensesDetail__table-header">
              <span className="OrderLicensesDetail__table-header-email">
                {t('mijn_vnl.orders.email_column')}
              </span>
              <span className="OrderLicensesDetail__table-header-status">
                {t('mijn_vnl.orders.status_column')}
                <InformationOverlay
                  overlayKey="status-info"
                  buttonIcon="question-circle"
                  buttonClassName="OrderLicensesDetail__status-info-button"
                  title={t('mijn_vnl.orders.status_column')}
                >
                  <StatusInfoContent t={t} />
                </InformationOverlay>
              </span>
              <span className="OrderLicensesDetail__table-header-action" />
            </div>

            {/* Invitation rows */}
            <div className="OrderLicensesDetail__table-body">
              {invitations.map((invitation: ElearningInvitation) => (
                <div key={invitation.id} className="OrderLicensesDetail__row">
                  <span className="OrderLicensesDetail__row-email">
                    {invitation.email}
                  </span>
                  <span className="OrderLicensesDetail__row-status">
                    <StatusIndicator status={invitation.status} />
                  </span>
                  <span className="OrderLicensesDetail__row-action">
                    <InformationOverlay
                      overlayKey={`invitation-edit-${invitation.id}`}
                      buttonIcon="chevron-right"
                      buttonClassName="OrderLicensesDetail__row-action-button"
                      title={t('mijn_vnl.orders.participant')}
                    >
                      <InvitationEditForm
                        invitation={invitation}
                        onSubmit={(email) => updateInvite(invitation.id, email)}
                        onDelete={invitation.status === 'invited'
                          ? () => deleteInvite(invitation.id)
                          : undefined}
                        errorMessage={invitationError.errorCode
                          ? t(getErrorMessageKey(invitationError.errorCode))
                          : null}
                      />
                    </InformationOverlay>
                  </span>
                </div>
              ))}
            </div>

            {/* Mobile-only status explanation link */}
            <InformationOverlay
              overlayKey="status-info-mobile"
              buttonClassName="OrderLicensesDetail__status-info-link"
              buttonIcon="question-circle"
              buttonTitle={t('mijn_vnl.orders.status_explanation')}
              title={t('mijn_vnl.orders.status_column')}
            >
              <StatusInfoContent t={t} />
            </InformationOverlay>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderLicensesDetail;
